import {
  ProjectStateAsString,
  ProjectStatus,
} from 'controllers/Project/ProjectStateMachine'
import deepMerge from 'deepmerge'
import _ from 'lodash'
import {
  AssemblyHeaderRow,
  AssemblyRow,
  BoMItemRow,
  PartTypeRow,
} from 'model/Project/BoMItemRow'
import { ProjectModel } from 'model/ProjectModel'
import {
  AssemblyHeaderDto,
  KeywordDto,
  PriceSummaryDto,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import {
  AssemblyStructureDto,
  IssueDto,
  ProjectDto,
  RowDto,
} from '../../services/APIs/InternalAPI/internal-api.contracts'
import { RecursivePartial } from '../../utils/RecursivePartial'
import { BoMItemsFilter, ProjectState } from './ProjectTypes'

/**
 * Given the local data fill up the filters with available values.
 * The keywords filter needs to call the server so it's not handled here.
 * @param assemblyHeaders assembly headers from server
 * @param bomItems local bom items
 * @param state current state merge the filters state
 */
export function defineLocalFilters(
  assemblyHeaders: AssemblyHeaderDto[],
  bomItems: Record<string, BoMItemRow>,
  state: ProjectState
): Partial<ProjectState['filters']> {
  const workingStepFilters = GetWorkingStepFilters(assemblyHeaders)

  const materialKeywordsAvailableFilters =
    GetMaterialKeywordsAvailableTokens(bomItems)

  const issues = Object.keys(bomItems).reduce((prev, current) => {
    if (bomItems[current]['issues']) {
      const issues: IssueDto[] = bomItems[current]['issues']

      prev.push(...issues)
    }

    return prev
  }, [] as IssueDto[])

  const filters: Partial<ProjectState['filters']> = {
    byIssues: {
      ...state.filters.byIssues,
      filter: {
        ...state.filters.byIssues.filter,
        availableFilters: _.uniqBy(issues, (x) => x.id),
      },
    },
    byWorkingStep: {
      ...state.filters.byWorkingStep,
      filter: {
        ...state.filters.byWorkingStep.filter,
        availableFilters: workingStepFilters,
        // enabledFilters: [],
        // disabledFilters: [],
      },
    },
    byMaterialKeywords: {
      ...state.filters.byMaterialKeywords,
      filter: {
        ...state.filters.byMaterialKeywords.filter,
        availableFilters: materialKeywordsAvailableFilters,
      },
    },
  }

  return filters
}

/**
 * return the working steps that should be show in WorkingStepFilter
 * @param assemblyHeaders assembly headers that came from server
 */
export function GetWorkingStepFilters(assemblyHeaders: AssemblyHeaderDto[]) {
  const workingStepFilters = new Set<WorkingStepType>()

  assemblyHeaders.forEach((x) => {
    _.union(
      x.assembly.partTypeWorkingStepDtos
      // x.assembly.assemblyWorkingStepDtos
    ).forEach((x) => workingStepFilters.add(x.primaryWorkingStep))
  })

  return Array.from(workingStepFilters)
}

export function SetFilter(
  state: ProjectState,
  boMItemFilter: BoMItemsFilter
): ProjectState {
  const newFilters: Partial<ProjectState['filters']> = {}

  switch (boMItemFilter.type) {
    case 'IssuesFilter': {
      newFilters.byIssues = {
        ...state.filters.byIssues,
        filter: {
          ...state.filters.byIssues.filter,
          ...boMItemFilter,
        },
      }
      break
    }
    case 'MaterialKeywordsFilter': {
      newFilters.byMaterialKeywords = {
        ...state.filters.byMaterialKeywords,
        filter: {
          ...state.filters.byMaterialKeywords.filter,
          ...boMItemFilter,
        },
      }
      break
    }
    case 'WorkingStepsFilter': {
      newFilters.byWorkingStep = {
        ...state.filters.byWorkingStep,
        filter: {
          ...state.filters.byWorkingStep.filter,
          ...boMItemFilter,
        },
      }
      break
    }
  }

  const newState = {
    ...state,
    filters: {
      ...state.filters,
      ...newFilters,
    },
  }

  return newState
}

const getFlatViewFromAPIResponse = (
  project: ProjectDto,
  currentState: Record<string, BoMItemRow>,
  selectedBomItemId: string
) => {
  const flatView: Record<string, BoMItemRow> = {}
  const rowsWithAssemblyId = {}

  project.boM.assemblyHeaders
    .filter((x) => x.assembly.partTypeIds.length > 0)
    .forEach((a) => {
      if (!a.assembly?.id) {
        return
      }

      const currentHeader: AssemblyHeaderRow =
        currentState &&
        currentState[a.assembly.id] &&
        ({ ...currentState[a.assembly.id] } as AssemblyHeaderRow)

      if (a.assembly.id === selectedBomItemId) {
        currentHeader.validationHighlight = true
      }

      // currentHeader && delete currentHeader['partTypeIds']
      // currentHeader && delete currentHeader.assembly.attachments

      // const assemblyHeader = _.merge({}, { type: 'AssemblyHeader' }, currentHeader, a)
      const assemblyHeader = Object.assign(
        {},
        {
          type: 'AssemblyHeader',
          isOpen:
            a.assembly.partTypeIds.findIndex(
              (bomItemId) => bomItemId === selectedBomItemId
            ) > -1 /*index === 0*/,
        },
        currentHeader || {
          // default open assembly header if it is individual parts
          isOpen: a?.assembly?.hasGeometry,
        },
        a
      ) as AssemblyHeaderRow
      flatView[a.assembly.id] = assemblyHeader

      assemblyHeader.assembly.partTypeIds.forEach((x) => {
        rowsWithAssemblyId[x] = { assemblyId: a.assembly.id }
      })
    })

  project.boM.materialHeaders.forEach((materialHeader) => {
    const newMaterialHeader = Object.assign(
      { type: 'MaterialHeader' },
      currentState ? currentState[materialHeader.id] : null,
      materialHeader
    )
    flatView[materialHeader.id] = newMaterialHeader
  })

  project.boM.partTypeRows.forEach((row) => {
    flatView[row.id] = Object.assign(
      { type: 'Row' },
      rowsWithAssemblyId[row.id],
      currentState?.flatView && currentState.flatView[row.id],
      row,
      {
        validationHighlight:
          selectedBomItemId === row.id || selectedBomItemId === row.id,
        operationPending: false,
      }
    )
  })

  project.boM.routingHeader.forEach((route) => {
    flatView[route.id] = Object.assign(
      { type: 'RoutingHeader' },
      currentState ? currentState[route.id] : null,
      route
    )
  })

  return flatView
}

const getPriceSummaryFromAPIResponse = (
  priceSummaries: PriceSummaryDto[]
): Record<string, PriceSummaryDto> => {
  const ps: Record<string, PriceSummaryDto> = {}

  priceSummaries
    .sort((a, b) => (a.description < b.description ? -1 : 1))
    .forEach((priceSummary) => {
      const priceSummaryKey = getPriceSummaryKey(priceSummary)
      ps[priceSummaryKey] = Object.assign(
        { type: 'PriceSummaryDto' },
        { ...priceSummary }
      )
    })

  return ps
}

const getAssemblyStructure = (
  rootAssembly: AssemblyStructureDto,
  parentId: string,
  selectedBomItemId: string,
  currentState: ProjectState
) => {
  if (!rootAssembly /*|| !rootAssembly.partTypeRows?.length*/) return null

  const assemblyBomItem: Record<string, AssemblyRow> = {
    [rootAssembly.assembly.id]: {
      ...((currentState?.treeView &&
        currentState.treeView[rootAssembly.assembly.id]) ||
        {}),
      ...rootAssembly.assembly,
      type: 'Assembly',
      parentId: parentId,
      originalFileName: rootAssembly.originalFileName,
      workingSteps: rootAssembly.workingStepDtos,
      validationHighlight: rootAssembly.id === selectedBomItemId,
      partTypeIds: rootAssembly.partTypeRows.map((x) => x.id),
      subAssembliesIds: rootAssembly.subAssemblies.map((x) => x.id),
    },
  }
  const partTypes: Record<string, PartTypeRow> = _.keyBy(
    rootAssembly.partTypeRows.map((x) => ({
      ...((currentState?.treeView && currentState?.treeView[x.id]) || {}),
      type: 'Row',
      validationHighlight: x.id === selectedBomItemId,
      ...x,
    })),
    (x) => x.id
  )

  // const subAssemblies = getAssemblyStructure(rootAssembly.subAssemblies.shift(), rootAssembly.id)
  let subAssemblies = {}
  rootAssembly.subAssemblies.forEach(
    (x) =>
      (subAssemblies = {
        ...subAssemblies,
        ...getAssemblyStructure(
          x,
          rootAssembly.id,
          selectedBomItemId,
          currentState
        ),
      })
  )

  return {
    ...assemblyBomItem,
    ...partTypes,
    ...subAssemblies,
  }
}

const getTreeViewFromAPIResponse = (
  project: ProjectDto,
  currentState: ProjectState,
  selectedBomItemId?: string
) => {
  if (!project.boM.assemblyStructures) {
    return null
  }

  let allItems = {}

  project.boM.assemblyStructures.forEach((structure) => {
    allItems = {
      ...allItems,
      ...getAssemblyStructure(structure, null, selectedBomItemId, currentState),
    }
  })

  return allItems
}

/**
 * return the normalized version of the projectState,
 * meaning instead of arrays, we will have a object
 * { [bomItemId]: RowDto | MaterialHeaderDto | AssemblyHeaderDto | PriceSummaryDto }
 * and the project object returned will not have the boM array
 * @param boM the bill of materials from server
 */
export function normalizeProjectState(
  project: ProjectDto,
  currentState: ProjectState,
  selectedBomItemId?: string
): Partial<ProjectState> {
  const flatView = getFlatViewFromAPIResponse(
    project,
    currentState.flatView,
    selectedBomItemId || currentState.selectedBomItemIds[0]
  )
  const treeView = getTreeViewFromAPIResponse(
    project,
    currentState,
    selectedBomItemId
  )

  const assemblyHeadersIds = project.boM.assemblyHeaders
    .filter((x) => x.assembly?.partTypeIds?.length > 0)
    .map((x) => x.assembly?.id)
  const materialHeadersIds = project.boM.materialHeaders
    .filter((x) => x.partTypeIds?.length > 0)
    .map((x) => x.id)
  const partTypesIds = project.boM.assemblyHeaders
    .map((x) => x.assembly.partTypeIds)
    .flat()
  const routingHeadersIds = project.boM.routingHeader
    .filter((x) => x.partTypeIds?.length > 0)
    .map((x) => x.id)

  const structuredPartTypesIds = []

  const subAssemblyIds: string[] = []
  const getSubAssembliesId = (assemblyStructures: AssemblyStructureDto[]) => {
    const getIds = (assemblyStructure: AssemblyStructureDto) => {
      if (assemblyStructure.subAssemblies?.length) {
        assemblyStructure.subAssemblies.forEach((sub) => getIds(sub))
      }
      subAssemblyIds.push(assemblyStructure.id)

      structuredPartTypesIds.push(
        ...assemblyStructure.partTypeRows.map((x) => x.id)
      )
    }

    assemblyStructures.forEach(getIds)
  }

  getSubAssembliesId(project.boM.assemblyStructures)

  const priceSummaries = getPriceSummaryFromAPIResponse(
    project.boM.priceSummaries
  )

  return {
    flatView,
    treeView,
    activeProject: compressProject(project),
    assemblyStructuresIds: _.uniq(subAssemblyIds),
    materialHeadersIds: materialHeadersIds,
    assemblyHeadersIds: assemblyHeadersIds,
    routingHeadersIds: routingHeadersIds,
    partTypeIds: partTypesIds,
    structurePartTypeIds: structuredPartTypesIds,
    priceSummaries,
  }
}

export function getPriceSummaryKey(priceSummary: PriceSummaryDto) {
  if (!priceSummary.materialId && !priceSummary.workingStep) {
    throw 'priceSummary should have a materialId or workingStep'
  }
  if (priceSummary.isMaterial) return priceSummary.materialId
  if (priceSummary.isPurchasePart) return priceSummary.materialId
  if (priceSummary.isPurchasePartWithoutArticle) return priceSummary.description
  else
    return `${priceSummary.workingStep.resource.id}_${priceSummary.workingStep.primaryWorkingStep}_${priceSummary.workingStep.secondaryWorkingStep}`
}

/**
 * will remove not needed data from boM array.
 * Since we are normalizing the bom state, we don't need duplicated data
 * @param project the projectDto from server
 */
function compressProject(project: ProjectDto) {
  const compressedProj = { ...project }
  delete compressedProj.boM

  return compressedProj
}

export function setActiveProjectProperty(
  state: ProjectState,
  properties: Partial<ProjectModel>
): ProjectState {
  if (!state.activeProject) return state

  const project = state.activeProject

  const newProject = deepMerge(project, properties)
  const newState = {
    ...state,
    activeProject: newProject,
  }

  return newState
}
export function setAssemblyProperty(
  state: ProjectState,
  assemblyId: string,
  property: RecursivePartial<AssemblyHeaderRow>
): ProjectState {
  const header = { ...state.flatView[assemblyId] }

  if (!header) return state

  if (header.type !== 'AssemblyHeader') {
    throw Error('should be an AssemblyHeader')
  }

  const newHeader = deepMerge(header, property, {
    arrayMerge: (target, source) => source,
  })

  return {
    ...state,
    flatView: Object.assign({}, state.flatView, {
      [newHeader.assembly.id]: { ...newHeader },
    }),
  }
}

export function setRowProperty(
  state: ProjectState,
  rowId: string,
  property: Partial<BoMItemRow>,
  arrayMergeStrategy?: (target: unknown[], source: unknown[]) => unknown[]
): ProjectState {
  let flatView = true

  let currentRow = { ...state.flatView[rowId] }

  if (_.isEmpty(currentRow)) {
    currentRow = { ...state.treeView[rowId] }
    flatView = false
  }

  if (!currentRow) return state

  const arrayMerge = arrayMergeStrategy
    ? arrayMergeStrategy
    : (target, source) => source

  const newRow = deepMerge<RowDto | AssemblyHeaderDto>(currentRow, property, {
    arrayMerge: arrayMerge,
  })

  return {
    ...state,
    [flatView ? 'flatView' : 'treeView']: Object.assign(
      {},
      flatView ? state.flatView : state.treeView,
      {
        [currentRow.type === 'AssemblyHeader'
          ? currentRow.assembly.id
          : currentRow.id]: newRow,
      }
    ),
  }
}

const lockedStates = [
  ProjectStateAsString.CANCELLED,
  ProjectStateAsString.ORDERED,
  ProjectStateAsString.PRODUCING,
  ProjectStateAsString.PRODUCED,
  ProjectStateAsString.PACKAGING,
  ProjectStateAsString.PACKAGED,
  ProjectStateAsString.DELIVERING,
  ProjectStateAsString.DELIVERED,
]

export function isTheProjectLocked(project: ProjectDto) {
  let isLocked = false

  const fix = project.status
    ? project.status.toUpperCase()
    : ''

  const projState = ProjectStateAsString[fix]

  if (lockedStates.includes(projState)) {
    isLocked = true
  } else if (
    project.isBuyingPartyView &&
    projState !== ProjectStatus.DEFINING
  ) {
    isLocked = true
  }

  return { projectIsLocked: isLocked, projectStatus: projState }
}
export function GetMaterialKeywordsAvailableTokens(
  bomItems: Record<string, BoMItemRow>
) {
  const availableTokens: Record<string, KeywordDto[]> = {}

  Object.keys(bomItems).forEach((key) => {
    if (bomItems[key].type === 'Row') {
      const bomItem = bomItems[key] as RowDto
      const keywords = bomItem.materialSummary.tokens

      Object.keys(keywords).forEach(
        (group) =>
          (availableTokens[group] = _.uniqBy(
            [...(availableTokens[group] || []), ...(keywords[group] || [])],
            (x) => x.originalKeyword
          ))
      )
    }
  })

  return availableTokens
}
